import { useTranslation } from "react-i18next";

function initials(name = "") {
  return name
    .split(/\s+/)
    .map((n) => n[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

function PhoneIcon({ className = "h-4 w-4" }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <path
        d="M22 16.92v2a2 2 0 0 1-2.18 2 19.8 19.8 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.92 4.2 2 2 0 0 1 4.92 2h2a2 2 0 0 1 2 1.72c.12.9.33 1.77.63 2.6a2 2 0 0 1-.45 2.11L8.09 9.54a16 16 0 0 0 6.37 6.37l1.1-1.01a2 2 0 0 1 2.11-.45c.83.3 1.7.51 2.6.63A2 2 0 0 1 22 16.92z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function MailIcon({ className = "h-4 w-4" }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <path
        d="M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="m22 6-10 7L2 6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function TeamList({
  limit,
  className = "",
  // `cols` kept for compatibility but layout is handled by flex centering now
  cols = "md:grid-cols-2 lg:grid-cols-3",
  size = "md",
}) {
  const { t } = useTranslation();
  const members = t("team.members", { returnObjects: true }) || [];
  const list = typeof limit === "number" ? members.slice(0, limit) : members;

  const cfg = {
    md: {
      gap: "gap-6",
      article: "rounded-2xl p-0",
      border: "border border-neutral-800",
      hover:
        "transition hover:border-white hover:-translate-y-1 hover:shadow-[0_0_0_1px_rgba(255,255,255,0.25)_inset,0_16px_40px_rgba(255,255,255,0.08)]",
      imgWrap: "aspect-[4/5]",
      initialsCircle: "h-28 w-28",
      name: "text-xl",
      role: "text-sm",
      pad: "p-6",
      contactText: "text-sm",
    },
    sm: {
      gap: "gap-4",
      article: "rounded-xl p-0",
      border: "border border-neutral-800",
      hover:
        "transition hover:border-white hover:-translate-y-0.5 hover:shadow-[0_0_0_1px_rgba(255,255,255,0.2)_inset,0_10px_28px_rgba(255,255,255,0.06)]",
      imgWrap: "aspect-[3/4]",
      initialsCircle: "h-20 w-20",
      name: "text-lg",
      role: "text-xs",
      pad: "p-4",
      contactText: "text-xs",
    },
    xs: {
      gap: "gap-3",
      article: "rounded-lg p-0",
      border: "border border-neutral-800",
      hover:
        "transition hover:border-white hover:-translate-y-0.5 hover:shadow-[0_0_0_1px_rgba(255,255,255,0.18)_inset,0_8px_20px_rgba(255,255,255,0.05)]",
      imgWrap: "aspect-[1/1]",
      initialsCircle: "h-16 w-16",
      name: "text-base",
      role: "text-xs",
      pad: "p-3",
      contactText: "text-xs",
    },
  }[size];

  return (
    <div className={`flex flex-wrap justify-center ${cfg.gap} ${className}`}>
      {list.map((m, i) => {
        const imgSrc = m?.image ? `/team/${m.image}` : null;
        const href = m?.linkedin?.trim();
        const email = m?.email?.trim();
        const phone = m?.phone?.trim();
        const phoneHref = phone ? `tel:${phone.replace(/[^\d+]/g, "")}` : null;

        return (
          <article
            key={`${m?.name || "member"}-${i}`}
            className={`group relative ${cfg.article} ${cfg.border} bg-black/30 backdrop-blur-sm overflow-hidden ${cfg.hover} w-full max-w-[22rem]`}
          >
            {href && (
              <a
                href={href}
                target="_blank"
                rel="noreferrer"
                aria-label={`${m?.name} — LinkedIn`}
                className="absolute inset-0 z-10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
              >
                <span className="sr-only">{m?.name} LinkedIn</span>
              </a>
            )}

            <div className={`relative w-full ${cfg.imgWrap} border-b border-neutral-800 overflow-hidden`}>
              <div className="absolute inset-0 flex items-center justify-center">
                <div
                  className={`${cfg.initialsCircle} rounded-full border border-neutral-800 bg-neutral-900 flex items-center justify-center`}
                >
                  <span
                    className={`${
                      size === "xs" ? "text-lg" : size === "sm" ? "text-xl" : "text-2xl"
                    } font-extrabold text-white/70 select-none`}
                  >
                    {initials(m?.name)}
                  </span>
                </div>
              </div>

              {imgSrc && (
                <img
                  src={imgSrc}
                  alt={m?.name || "Team member"}
                  className="absolute inset-0 h-full w-full object-cover grayscale-[30%] contrast-125 transition group-hover:grayscale-0"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                  draggable="false"
                />
              )}

              <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-white/5 via-transparent to-transparent" />
            </div>

            <div className={`${cfg.pad} pt-3`}>
              <h3 className={`${cfg.name} font-semibold leading-tight`}>{m?.name || "—"}</h3>
              <p className={`${cfg.role} text-muted mt-1`}>{m?.role || ""}</p>

              {(email || phone) && (
                <div className={`mt-3 space-y-1 ${cfg.contactText}`}>
                  {email && (
                    <div className="flex items-center gap-2">
                      <MailIcon />
                      <a className="underline break-all relative z-20" href={`mailto:${email}`}>
                        {email}
                      </a>
                    </div>
                  )}
                  {phone && (
                    <div className="flex items-center gap-2">
                      <PhoneIcon />
                      <a className="underline relative z-20" href={phoneHref}>
                        {phone}
                      </a>
                    </div>
                  )}
                </div>
              )}
            </div>
          </article>
        );
      })}
    </div>
  );
}
